/* eslint-disable no-console */
import { presenceDocumentTypes, presenceImageTypes, presenceVideoTypes } from './Firebase/firebaseQuery';
import { addMessagesToLocalDb } from './LocalDB';
import { store } from '../redux/store';

/**
 * Add attachment type classification to message for LocalDB storage
 */
export const addAttachmentTypeForLocalDb = (msg) =>
{
	const msgExtention = msg?.attachments?.type?.toLowerCase();
	let attachmentTypeValue = '';

	if (presenceImageTypes?.includes(msgExtention))
	{
		attachmentTypeValue = 'image';
	}
	else if (presenceVideoTypes?.includes(msgExtention))
	{
		attachmentTypeValue = 'video';
	}
	else if (presenceDocumentTypes?.includes(msgExtention))
	{
		attachmentTypeValue = 'doc';
	}

	return { ...msg, attachmentType: attachmentTypeValue };
};

/**
 * Apply auto-download settings to a message based on user preferences
 */
export const applyAutoDownloadSettings = (msg, autoDownloadSettings) =>
{
	if (!msg?.attachments || !autoDownloadSettings)
	{
		return msg;
	}

	const autoDownloadMapping = {
		isPhotosAutoDownload    : 'isPhotosAutoDownload',
		isDocumentsAutoDownload : 'isDocAutoDownload',
		isAudioAutoDownload     : 'isAudioAutoDownload',
		isVideoAutoDownload     : 'isVideoAutoDownload'
	};

	let processedMsg = { ...msg };

	for (const [ settingKey, dataKey ] of Object.entries(autoDownloadMapping))
	{
		processedMsg = {
			...processedMsg,
			[dataKey] : autoDownloadSettings?.[settingKey]
		};
	}

	return processedMsg;
};

/**
 * Process an array of messages with attachment types and auto-download settings
 * @param {Array} messages - Array of message objects
 * @param {Object} options - Processing options
 * @param {boolean} options.applyAutoDownload - Whether to apply auto-download settings
 * @returns {Array} Processed messages ready for IndexedDB
 */
export const processMessagesForLocalDb = (messages, options = {}) =>
{
	const { applyAutoDownload = true } = options;

	const myPresence = store?.getState()?.mypresence;
	const autoDownloadSettings = myPresence?.userAutoDownloadSetting;

	return messages.map((msg) =>
	{
		let processedMsg = { ...msg };

		// Apply auto-download settings if enabled
		if (applyAutoDownload && autoDownloadSettings)
		{
			processedMsg = applyAutoDownloadSettings(processedMsg, autoDownloadSettings);
		}

		// Add attachment type classification
		processedMsg = addAttachmentTypeForLocalDb(processedMsg);

		return processedMsg;
	});
};

/**
 * Save messages to IndexedDB and update presenceTimestamp
 * This mirrors the logic in messagesDbCallback for consistency
 */
export const saveMessagesWithTimestamp = async (messages) =>
{
	if (!messages || messages.length === 0)
	{
		return;
	}

	try
	{
		await addMessagesToLocalDb(messages);

		// Use the latest message's updatedAt timestamp instead of client time
		// This prevents time synchronization issues between client and server
		const latestTimestamp = messages.reduce((max, msg) =>
		{
			const msgTime = msg?.updatedAt || 0;

			return msgTime > max ? msgTime : max;
		}, 0);

		if (latestTimestamp > 0)
		{
			const currentTimestamp = parseInt(localStorage.getItem('presenceTimestamp') || '0');

			// Only update if the new timestamp is newer
			if (latestTimestamp > currentTimestamp)
			{
				localStorage.setItem('presenceTimestamp', JSON.stringify(latestTimestamp));
			}
		}
	}
	catch (error)
	{
		console.error('Error saving messages to IndexedDB:', error);
		// Don't update timestamp if save failed - will retry these messages on next sync
		throw error;
	}
};

/**
 * Process and save messages to IndexedDB with all necessary transformations
 * @param {Array} messages - Raw messages from Firebase
 * @param {Object} options - Processing options
 * @returns {Array} Processed messages
 */
export const processAndSaveMessages = async (messages, options = {}) =>
{
	if (!messages || messages.length === 0)
	{
		return [];
	}

	// Process messages with attachment types and auto-download settings
	const processedMessages = processMessagesForLocalDb(messages, options);

	// Save to IndexedDB and update timestamp
	await saveMessagesWithTimestamp(processedMessages);

	return processedMessages;
};
